import React, { useState } from 'react'
import { Button, Typography } from '@mui/material'
import {
  usePasswordChangeMutation,
  selectCurrentUser,
  useResend2FAMutation,
  errorSnack,
  successSnack,
} from 'features'
import { useDispatch, useSelector } from 'react-redux'
import DialogBase from './DialogBase'
import { useForm } from 'react-hook-form'
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  PrimaryCta,
  PasswordInput,
  Input,
  SecondaryCta,
  SubmitSending,
} from 'components'
import { maskCharacters } from 'utilities/mask-characters'
import { usePasswordComplexityCheck } from 'hooks'

export default function ChangeActiveUserPasswordDialog(_props) {
  const {
    togglerComponent: Component = { Button },
    children,
    togglerProps = {},
  } = _props

  const userAuthenticated = useSelector(selectCurrentUser)
  const [resend2FA, { isLoading: resendIsLoading }] = useResend2FAMutation()
  const [codeSent, setCodeSent] = useState(false)

  const [open, setOpen] = useState(false)

  const dispatch = useDispatch()

  function openDialog(e) {
    setOpen(true)
  }
  function closeDialog() {
    setOpen(false)
  }

  const send2faButtonLabel = codeSent ? 'Reenviar código' : 'Enviar código'

  const codeSentTo =
    userAuthenticated?.twofa !== 0 || userAuthenticated?.twofa !== "0"
      ? `teléfono terminado en ${maskCharacters(
          userAuthenticated?.phone || '',
          '*',
          2
        )}`
      : 'correo electrónico'

  const [passwordChanged, setPasswordChanged] = React.useState(false)

  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      oldPassword: '',
      passwordConfirmation: '',
      password: '',
      code: '',
    },
  })

  const [isPasswordValid, { PasswordRequirements }] =
    usePasswordComplexityCheck(watch('password'))

  const [updatePassword, { isLoading }] = usePasswordChangeMutation()

  function onSubmit(data) {
    const mutation = updatePassword({
      oldPassword: data.oldPassword,
      newPassword: data.password,
      number: parseInt(data.code),
      email: userAuthenticated.email,
    }).unwrap()
    mutation
      .then((response) => {
        if (response?.message === 'not_aproved') {
          dispatch(
            errorSnack({
              message: `El código de verificación no es válido`,
            })
          )
          setError('code', {
            type: 'custom',
            message:
              'El código de verificación no es válido, puedes solicitar uno nuevo',
          })
        } else if (response?.bouserid) {
          dispatch(
            successSnack({
              message: `La contraseña ha sido cambiada correctamente`,
            })
          )
          setPasswordChanged(true)
        }
      })
      .catch((error) => {
        if (error?.data?.message === 'not_match') {
          dispatch(
            errorSnack({
              message: `La contraseña anterior no coincide`,
            })
          )
          setError('oldPassword', {
            type: 'custom',
            message: 'La contraseña actual no es correcta',
          })
        }
        if (error?.data?.message === 'not_valid_resended') {
          dispatch(
            errorSnack({
              message: `El código de verificación ha expirado, hemos enviado un nuevo código a tu ${codeSentTo}`,
            })
          )
          setError('code', {
            type: 'custom',
            message: 'El código de verificación no es correcto',
          })
        }
      })
  }

  function passwordConfirmationIsValid(value) {
    return watch('password') === watch('passwordConfirmation')
      ? true
      : 'Las contraseñas no coinciden'
  }

  const resend2FAHandler = async () => {
    try {
      const response = await resend2FA({
        email: userAuthenticated.email,
      }).unwrap()
      if (response?.message === 'code_sent') {
        dispatch(
          successSnack({
            message: `Se ha enviado un nuevo código a tu ` + codeSentTo,
          })
        )
        setCodeSent(true)
      }
    } catch (err) {}
  }

  return (
    <>
      <Component onClick={openDialog} {...togglerProps}>
        {children}
      </Component>
      {open && (
        <DialogBase
          title={
            !passwordChanged ? 'Cambiar contraseña' : 'Contraseña cambiada'
          }
          open={open}
          handleClose={closeDialog}
        >
          {!passwordChanged ? (
            <>
              <Typography variant="body1" className="mb-8">
                Elige una nueva contraseña y usa el código de verificación que
                te enviamos a tu correo
              </Typography>
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col justify-start items-start gap-4 bg-white tests"
                autoComplete="off"
              >
                <PasswordInput
                  control={control}
                  name="oldPassword"
                  label="Contraseña Actual"
                  errors={errors.oldPassword}
                  validation={{
                    required: 'La contraseña actual es requerida',
                  }}
                />
                <PasswordInput
                  control={control}
                  name="password"
                  label="Nueva Contraseña"
                  errors={errors.password}
                  validation={{
                    required: 'La contraseña es requerida',
                    validate: isPasswordValid,
                  }}
                />
                <PasswordRequirements />
                <PasswordInput
                  control={control}
                  name="passwordConfirmation"
                  label="Repetir nueva contraseña"
                  errors={errors.passwordConfirmation}
                  validation={{
                    required:
                      'La confirmacion de contraseña es requerida y debe ser igual a la contraseña',
                    validate: passwordConfirmationIsValid,
                  }}
                />
                <Input
                  control={control}
                  name="code"
                  type="text"
                  label="Código de verificación"
                  errors={errors.code}
                  validation={{
                    required:
                      'Por favor ingresa el código de verificación que enviamos a tu correo',
                  }}
                />
                <Button variant="text" onClick={resend2FAHandler}>
                  {resendIsLoading ? (
                    <SubmitSending label="Enviando código" />
                  ) : (
                    send2faButtonLabel
                  )}
                </Button>
                <div className="flex flex-row justify-center gap-4 w-full">
                  <div className="w-1/2">
                    <SecondaryCta onClick={closeDialog} fullWidth>
                      Cancelar
                    </SecondaryCta>
                  </div>
                  <div className="w-1/2">
                    <PrimaryCta
                      type="submit"
                      fullWidth
                      isSubmitting={isLoading}
                      submittingLabel="Enviando"
                    >
                      Enviar
                    </PrimaryCta>
                  </div>
                </div>
              </form>
            </>
          ) : (
            <>
              <Typography
                variant="h1"
                component="div"
                className="mb-8 text-success text-center opacity-50"
              >
                <FontAwesomeIcon icon={faCheckCircle} size="xl" />
              </Typography>
              <Typography variant="body1" className="mb-8 text-center">
                Tu contraseña ha sido cambiada con éxito
              </Typography>

              <PrimaryCta onClick={closeDialog} fullWidth>
                Cerrar
              </PrimaryCta>
            </>
          )}
        </DialogBase>
      )}
    </>
  )
}
